import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLink, faUnlink, faLayerGroup, faCheckCircle, faClock, faTimes, faFlagCheckered, faPlus, faEdit, faTrash,
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import SearchableSelect from '../../../components/SearchableSelect';
import SearchableMultiSelect from '../../../components/SearchableMultiSelect';
import TerrainTransitionModal from './TerrainTransitionModal';
import VagueBadge from './VagueBadge';
import ConfirmModal from './ConfirmModal';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

const SurveySlotsTab = ({ projectId, readOnly }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ waves: [], standalone_slots: [] });
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [linkingSlotId, setLinkingSlotId] = useState(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [unlinkModal, setUnlinkModal] = useState({ open: false, slot: null });
  const [unlinking, setUnlinking] = useState(false);
  const [deleteSlotModal, setDeleteSlotModal] = useState({ open: false, slot: null });
  const [deletingSlot, setDeletingSlot] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignModal, setAssignModal] = useState({ open: false, slot: null, selectedIds: [] });
  const [assignSaving, setAssignSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingSaving, setAddingSaving] = useState(false);
  const [errorModal, setErrorModal] = useState({ open: false, message: '', reasons: [] });

  // Assign modal carries a multi-select: only Escape and the buttons dismiss it.
  const assignDismiss = useModalDismiss(
    () => !assignSaving && setAssignModal({ open: false, slot: null, selectedIds: [] }),
    { backdropClose: false, enabled: assignModal.open && !assignSaving });
  const unlinkDismiss = useModalDismiss(
    () => !unlinking && setUnlinkModal({ open: false, slot: null }),
    { enabled: unlinkModal.open && !unlinking });

  const fetchSlots = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const res = await api.get(`/projects/${projectId}/slots`);
      setData(res.data.data || { waves: [], standalone_slots: [] });
    } catch (err) {
      console.error('Error loading slots:', err);
    } finally {
      if (isInitial) setLoading(false);
    }
  }, [projectId]);

  const fetchSurveys = useCallback(async () => {
    try {
      const res = await api.get('/projects/surveys-list');
      setSurveys(res.data.data || []);
    } catch (err) {
      console.error('Error loading surveys:', err);
    }
  }, []);

  useEffect(() => { fetchSlots(true); fetchSurveys(); }, [fetchSlots, fetchSurveys]);

  useEffect(() => {
    api.get('/projects/assignable-users').then(res => setAssignableUsers(res.data.data || [])).catch(() => {});
  }, []);

  const handleLink = async (slotId) => {
    if (!selectedSurveyId) return;
    try {
      await api.put(`/projects/${projectId}/slots/${slotId}/link`, { survey_id: selectedSurveyId });
      setLinkingSlotId(null);
      setSelectedSurveyId(null);
      fetchSlots();
    } catch (err) {
      setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur lors du lien' });
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkModal.slot) return;
    setUnlinking(true);
    try {
      await api.put(`/projects/${projectId}/slots/${unlinkModal.slot.id}/unlink`);
      setUnlinkModal({ open: false, slot: null });
      fetchSlots();
    } catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' }); }
    finally { setUnlinking(false); }
  };

  const [togglingFinished, setTogglingFinished] = useState(null);

  const handleToggleFinished = async (slotId) => {
    setTogglingFinished(slotId);
    try {
      await api.post(`/projects/${projectId}/slots/${slotId}/toggle-finished`);
      fetchSlots();
    } catch (err) {
      const data = err.response?.data;
      setErrorModal({ open: true, message: data?.message || 'Erreur', reasons: data?.reasons || [] });
    }
    finally { setTogglingFinished(null); }
  };

  const handleDeleteSlot = async () => {
    if (!deleteSlotModal.slot) return;
    setDeletingSlot(true);
    try {
      await api.delete(`/projects/${projectId}/slots/${deleteSlotModal.slot.id}`);
      setDeleteSlotModal({ open: false, slot: null });
      fetchSlots();
    } catch (err) {
      const data = err.response?.data;
      setErrorModal({ open: true, message: data?.message || 'Erreur lors de la suppression', reasons: [] });
    }
    finally { setDeletingSlot(false); }
  };

  const openAssignModal = (slot) => {
    setAssignModal({ open: true, slot, selectedIds: (slot.assignees || []).map(a => a.id) });
  };

  const confirmAssign = async () => {
    if (!assignModal.slot) return;
    setAssignSaving(true);
    try {
      await api.put(`/projects/${projectId}/slots/${assignModal.slot.id}/assign`, { assignee_ids: assignModal.selectedIds });
      setAssignModal({ open: false, slot: null, selectedIds: [] });
      fetchSlots();
    } catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' }); }
    finally { setAssignSaving(false); }
  };

  const handleAddSlots = async ({ waves, standalone_slots }) => {
    setAddingSaving(true);
    try {
      if ((waves?.length > 0) || (standalone_slots?.length > 0)) {
        await api.post(`/projects/${projectId}/slots`, { waves, standalone_slots });
      }
      setShowAddModal(false);
      fetchSlots();
    } catch (err) { setErrorModal({ open: true, message: err.response?.data?.message || 'Erreur' }); }
    finally { setAddingSaving(false); }
  };


  const allLinkedSurveyIds = new Set();
  data.waves?.forEach(w => w.survey_slots?.forEach(s => { if (s.survey_id) allLinkedSurveyIds.add(s.survey_id); }));
  data.standalone_slots?.forEach(s => { if (s.survey_id) allLinkedSurveyIds.add(s.survey_id); });
  const availableSurveys = surveys.filter(s => !allLinkedSurveyIds.has(s.id));

  const allSlots = [
    ...(data.waves?.flatMap(w => w.survey_slots || []) || []),
    ...(data.standalone_slots || []),
  ];
  const linkedCount = allSlots.filter(s => s.survey_id).length;
  const finishedCount = allSlots.filter(s => s.is_finished).length;

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" /></div>;
  }

  const renderSlot = (slot) => {
    // Pivot rows are the source of truth; fall back to the legacy single survey so an
    // older payload still renders.
    const links = (slot.linked_surveys?.length || 0) > 0
      ? slot.linked_surveys
      : (slot.survey ? [{ id: 'primary', survey: slot.survey, survey_wave: slot.survey_wave }] : []);
    const isLinked = links.length > 0;
    const isLinking = linkingSlotId === slot.id;

    // No inline z-index here: the in-row survey dropdown it used to stack for now lives
    // in SlotEdit, and a value of 100 sat above the fixed header (z-50), which made these
    // rows paint over the notifications panel.
    return (
      <div key={slot.id} className="slots-row"
      onClick={() => navigate(`/projects/${projectId}/slots/${slot.id}`)}
      >
        {/* Status icon */}
        <div style={{ flexShrink: 0 }}>
          {slot.is_finished ? (
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon icon={faFlagCheckered} size="xs" style={{ color: '#16a34a' }} />
            </div>
          ) : isLinked ? (
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon icon={faCheckCircle} size="xs" style={{ color: '#2563eb' }} />
            </div>
          ) : (
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon icon={faClock} size="xs" style={{ color: '#a16207' }} />
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: slot.is_finished ? '#6b7280' : '#111827' }}>{slot.name}</span>
            {slot.is_finished && (
              <span style={{ fontSize: '0.58rem', fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: '#dcfce7', color: '#166534' }}>TERMINÉE</span>
            )}
          </div>
          {isLinked ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 1 }}>
              {links.map(l => (
                <div key={l.id} style={{ fontSize: '0.72rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{l.survey?.title}</span>
                  <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>({l.survey?.limesurvey_id})</span>
                  <span style={{ fontSize: '0.6rem', fontWeight: 600, padding: '0 6px', borderRadius: 8, background: l.survey?.poste_qcode === 'ref' ? '#dbeafe' : '#f3e8ff', color: l.survey?.poste_qcode === 'ref' ? '#1e40af' : '#6b21a8' }}>
                    {l.survey?.poste_qcode === 'ref' ? 'F2F' : 'CATI'}
                  </span>
                  <VagueBadge wave={l.survey_wave} style={{ fontSize: '0.6rem', padding: '0 6px', borderRadius: 8 }} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '0.7rem', color: '#a16207', marginTop: 1 }}>En attente de création</div>
          )}
          {/* Assigned names inline */}
          {(slot.assignees && slot.assignees.length > 0) && (
            <div onClick={() => openAssignModal(slot)} style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 2, cursor: 'pointer' }}>
              {slot.assignees.map(a => a.name).join(', ')}
            </div>
          )}
        </div>

        {/* Actions */}
        {!readOnly && <div className="slots-actions" onClick={e => e.stopPropagation()}>
          {/* Edit button — only for authorized users */}
          {slot.can_edit && (
            <button onClick={() => navigate(`/projects/${projectId}/slots/${slot.id}/edit`)}
              style={{ fontSize: '0.7rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faEdit} size="xs" className="me-1" />Modifier
            </button>
          )}
          {/* Delete button — setup-step slots only */}
          {slot.can_edit && slot.step === 'setup' && !slot.is_finished && (
            <button onClick={() => setDeleteSlotModal({ open: true, slot })}
              style={{ fontSize: '0.7rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faTrash} size="xs" className="me-1" />Supprimer
            </button>
          )}
          {/* Terminer toggle — only for linked surveys */}
          {isLinked && (
            <button
              onClick={() => handleToggleFinished(slot.id)}
              disabled={togglingFinished === slot.id}
              style={{
                fontSize: '0.7rem', padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                border: slot.is_finished ? '1px solid #10b981' : '1px solid #d1d5db',
                background: slot.is_finished ? '#f0fdf4' : '#fff',
                color: slot.is_finished ? '#166534' : '#6b7280',
              }}
            >
              <FontAwesomeIcon icon={faFlagCheckered} size="xs" className="me-1" />
              {slot.is_finished ? 'Terminée' : 'Terminer'}
            </button>
          )}
          {isLinked && !slot.is_finished && (
            <button onClick={() => setUnlinkModal({ open: true, slot })}
              style={{ fontSize: '0.7rem', padding: '4px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faUnlink} size="xs" className="me-1" />Délier
            </button>
          )}
        </div>}
      </div>
    );
  };

  return (
    <div className="slots-tab">
      {/* Summary bar */}
      <div className="slots-summary-bar">
        <div className="slots-summary-stats">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{allSlots.length}</div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Enquêtes</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#16a34a' }}>{finishedCount}</div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Terminées</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#2563eb' }}>{linkedCount}</div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Liées</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#a16207' }}>{allSlots.length - linkedCount}</div>
            <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>En attente</div>
          </div>
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setShowAddModal(true)}
              style={{ fontSize: '0.72rem', fontWeight: 600, padding: '6px 12px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FontAwesomeIcon icon={faPlus} size="xs" /> Ajouter
            </button>
          </div>
        )}
      </div>

      {/* Waves */}
      {(data.waves || []).map(wave => (
        <div key={wave.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 12 }}>
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faLayerGroup} size="sm" style={{ color: '#9ca3af' }} />
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{wave.name}</span>
            <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{(wave.survey_slots || []).length} enquête(s)</span>
          </div>
          <div style={{ padding: '0 20px' }}>
            {(wave.survey_slots || []).map(s => renderSlot(s))}
          </div>
        </div>
      ))}

      {/* Standalone */}
      {(data.standalone_slots || []).length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Enquêtes hors groupe</span>
          </div>
          <div style={{ padding: '0 20px' }}>
            {data.standalone_slots.map(s => renderSlot(s))}
          </div>
        </div>
      )}

      {allSlots.length === 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '50px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Aucun slot d'enquête défini.</div>
          <div style={{ fontSize: '0.75rem', color: '#d1d5db', marginTop: 4 }}>Les slots sont créés lors du passage en Terrain.</div>
        </div>
      )}

      {/* Add slots modal */}
      <TerrainTransitionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onConfirm={handleAddSlots}
        project={{ id: projectId }}
        loading={addingSaving}
        addOnly
      />

      {/* Assign modal */}
      {assignModal.open && assignModal.slot && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.4)',
        }} {...assignDismiss}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            animation: 'assignModalIn 0.2s ease',
            display: 'flex', flexDirection: 'column',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>
                  {assignModal.selectedIds.length > 1 ? 'Chargé(e)s d\'étude' : 'Chargé(e) d\'étude'}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>{assignModal.slot.name}</div>
              </div>
              <button onClick={() => setAssignModal({ open: false, slot: null, selectedIds: [] })} disabled={assignSaving}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div style={{ padding: 20, flex: 1, overflow: 'visible' }}>
              <SearchableMultiSelect
                options={assignableUsers}
                selectedValues={assignModal.selectedIds}
                onChange={ids => setAssignModal(prev => ({ ...prev, selectedIds: ids }))}
                placeholder="Rechercher et ajouter..."
                getOptionLabel={u => u.name}
                getOptionMeta={u => u.role_display || u.role}
                triggerClassName="px-3 py-2"
              />
            </div>
            <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button onClick={() => setAssignModal({ open: false, slot: null, selectedIds: [] })} disabled={assignSaving}
                style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={confirmAssign} disabled={assignSaving}
                style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                {assignSaving ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
          <style>{`@keyframes assignModalIn { from { opacity:0; transform:translateY(10px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
        </div>
      )}

      {/* Unlink confirmation modal */}
      {unlinkModal.open && unlinkModal.slot && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }}
          {...unlinkDismiss}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 420,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden',
              animation: 'unlinkModalIn 0.2s ease',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>Délier l'enquête</span>
              <button onClick={() => setUnlinkModal({ open: false, slot: null })} disabled={unlinking}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                {unlinkModal.slot.name}
              </div>

              {unlinkModal.slot.survey && (
                <div style={{
                  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', marginTop: 12,
                }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Enquête liée</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827' }}>{unlinkModal.slot.survey.title}</span>
                    <span style={{
                      fontSize: '0.6rem', fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                      background: unlinkModal.slot.survey.poste_qcode === 'ref' ? '#dbeafe' : '#f3e8ff',
                      color: unlinkModal.slot.survey.poste_qcode === 'ref' ? '#1e40af' : '#6b21a8',
                    }}>
                      {unlinkModal.slot.survey.poste_qcode === 'ref' ? 'F2F' : 'CATI'}
                    </span>
                  </div>
                  {unlinkModal.slot.survey.general_target && (
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                      Objectif: {unlinkModal.slot.survey.general_target} · LimeSurvey ID: {unlinkModal.slot.survey.limesurvey_id || 'N/A'}
                    </div>
                  )}
                </div>
              )}

              <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 12 }}>
                L'enquête ne sera pas supprimée, seulement détachée.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '1px solid #f3f4f6' }}>
              <button onClick={() => setUnlinkModal({ open: false, slot: null })} disabled={unlinking}
                style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={confirmUnlink} disabled={unlinking}
                style={{
                  padding: '7px 16px', borderRadius: 6, border: 'none',
                  background: unlinking ? '#e5e7eb' : '#dc2626',
                  color: unlinking ? '#9ca3af' : '#fff',
                  fontSize: '0.78rem', fontWeight: 600, cursor: unlinking ? 'default' : 'pointer',
                }}>
                {unlinking ? 'En cours...' : 'Délier'}
              </button>
            </div>
          </div>
          <style>{`@keyframes unlinkModalIn { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
        </div>
      )}
      <ConfirmModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal({ open: false, message: '', reasons: [] })}
        title={errorModal.message || 'Erreur'}
        message={errorModal.reasons?.length > 0 ? errorModal.reasons.join(' · ') : errorModal.message}
        variant="danger"
      />
      <ConfirmModal
        isOpen={deleteSlotModal.open}
        onClose={() => !deletingSlot && setDeleteSlotModal({ open: false, slot: null })}
        onConfirm={handleDeleteSlot}
        title="Supprimer cette enquête ?"
        message={deleteSlotModal.slot ? `« ${deleteSlotModal.slot.name} » sera supprimée définitivement avec sa checklist. Cette action est irréversible.` : ''}
        variant="danger"
        confirmLabel={deletingSlot ? 'Suppression...' : 'Supprimer'}
      />
      <style>{`
        .slots-summary-bar {
          display: flex; align-items: center; justify-content: space-between;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 20px; margin-bottom: 16px;
          flex-wrap: wrap; gap: 12px;
        }
        .slots-summary-stats { display: flex; gap: 24px; flex-wrap: wrap; }
        .slots-row {
          display: flex; align-items: center; gap: 12px; padding: 12px 0;
          border-bottom: 1px solid #f3f4f6; cursor: pointer; position: relative;
        }
        .slots-actions { flex-shrink: 0; display: flex; gap: 4px; flex-wrap: wrap; }
        @media (max-width: 768px) {
          .slots-summary-bar { padding: 10px 14px; flex-direction: column; align-items: flex-start; }
          .slots-summary-stats { gap: 16px; }
          .slots-row { flex-wrap: wrap; gap: 8px; }
          .slots-actions { width: 100%; justify-content: flex-start; margin-top: 4px; }
        }
      `}</style>
    </div>
  );
};

export default SurveySlotsTab;
